import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Edit3, Save, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  academyGetCourse, academyEditDraft, academyRoundtripCheck, academyOpenExportZip,
} from '../../../services/api';

/**
 * Admin → simple draft-only course editor.
 *
 * Immutability rule enforced by the backend: the API returns 409 if
 * this version isn't a draft. UI mirrors that guard by pre-checking
 * status before enabling Save.
 *
 * The editor operates on the parsed content JSON — that is the same
 * shape as `course.json` inside the package. For deeper structural
 * changes admins still export the package and re-import; the editor's
 * purpose is small in-portal edits (fix a typo, swap an answer,
 * change the review interval).
 */
export default function CourseEditor() {
  const { courseId, version } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [rtCheck, setRtCheck] = useState(null);

  const load = async () => {
    try {
      const c = await academyGetCourse(courseId, version);
      setCourse(c);
      setText(JSON.stringify(c.content, null, 2));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    }
  };
  useEffect(() => { load(); }, [courseId, version]);

  const isDraft = course?.status === 'draft';

  const save = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      toast.error('Invalid JSON: ' + e.message);
      return;
    }
    setSaving(true);
    try {
      const res = await academyEditDraft(courseId, version, {
        course: parsed.course,
        modules: parsed.modules,
        assessment: parsed.assessment,
        completion: parsed.completion,
        certificate: parsed.certificate,
      });
      toast.success(`Saved. Content hash: ${res.content_hash?.slice(0, 16)}…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const checkRoundtrip = async () => {
    try {
      const r = await academyRoundtripCheck(courseId, version);
      setRtCheck(r);
      if (r.ok && r.hash_matches) toast.success('Round-trip clean — hash matches');
      else toast.warning('Round-trip mismatch — see details');
    } catch (e) {
      toast.error('Round-trip failed');
    }
  };

  if (!course) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 space-y-3" data-testid="course-editor">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <Edit3 className="h-3.5 w-3.5" />Lutrom Academy
          </div>
          <h1 className="text-2xl font-display font-bold">Edit: {course.title} v{course.version}</h1>
          <p className="text-sm text-slate-600">
            Status: <b>{course.status}</b>{!isDraft && ' — editing is disabled on non-draft versions. Use "New draft".'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                await academyOpenExportZip(courseId, version);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not export course');
              }
            }}
            data-testid="editor-export-btn"
          >
            Export .zip
          </Button>
          <Button variant="outline" onClick={checkRoundtrip} data-testid="editor-roundtrip-btn">
            <RefreshCw className="h-3 w-3 mr-1" />Round-trip check
          </Button>
          <Button onClick={save} disabled={!isDraft || saving} data-testid="editor-save-btn">
            <Save className="h-3 w-3 mr-1" />{saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {rtCheck && (
        <div className={`text-xs rounded px-3 py-2 ${rtCheck.ok && rtCheck.hash_matches ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
          {rtCheck.ok
            ? `Round-trip ${rtCheck.hash_matches ? 'CLEAN' : 'MISMATCH'} · original ${rtCheck.original_hash?.slice(0, 16)} vs ${rtCheck.roundtrip_hash?.slice(0, 16)}`
            : `Round-trip failed: ${JSON.stringify(rtCheck.errors)}`}
        </div>
      )}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!isDraft}
        className="w-full h-[70vh] font-mono text-xs border rounded p-3 disabled:bg-slate-50"
        data-testid="editor-textarea"
      />
    </div>
  );
}
